import {
  MutationCache,
  QueryCache,
  QueryClient,
  hashKey,
} from '@tanstack/react-query';

import { ApiError, isAuthRoute } from '@/lib/api/errors';
import type { CurrentUser } from '@/lib/api/domains/auth';
import { keys } from '@/lib/query-keys';
import { useAuthStore } from '@/lib/session/auth-store';
import { announceRefusal } from '@/lib/stores/refusal-announcement-store';
import { telemetry } from '@/lib/telemetry';
import { getRouter } from './router-ref';

/*
 * Global 4xx interception lives here, once (spec §3.5). Components never handle
 * 401/403. A module-level redirect guard prevents cascades.
 */

/*
 * How long a redirect may be in flight before we assume it wedged. If the
 * navigation promise never settles (router replaced, unhandled rejection
 * swallowed, etc.) the watchdog resets the guard and reports it, so a single
 * stuck redirect can't permanently suppress every later auth redirect.
 */
const REDIRECT_WATCHDOG_MS = 5_000;

let redirecting = false;

/*
 * Fire at most one redirect at a time. The guard is released only once the
 * navigation SETTLES (in `.finally()`), not on a blind timer, so concurrent
 * 401s collapse to one redirect instead of racing. A watchdog is the backstop:
 * if the navigation never settles it resets the guard and records a warning
 * event rather than leaving auth redirects wedged forever.
 *
 * `destination` is captured by the caller BEFORE the cache/session is cleared,
 * so the redirect carries the pre-expiry page — never `/login` itself.
 */
function guardedRedirect(to: string, destination: string | undefined): void {
  if (redirecting) return;
  redirecting = true;

  let released = false;
  const release = (): void => {
    released = true;
    redirecting = false;
  };

  const watchdog = setTimeout(() => {
    if (released) return;
    telemetry.recordEvent('auth.redirect_guard_stuck', { to });
    redirecting = false;
  }, REDIRECT_WATCHDOG_MS);

  const router = getRouter();
  if (router) {
    const search = destination === undefined ? {} : { redirect: destination };
    void router.navigate({ to, search }).finally(() => {
      clearTimeout(watchdog);
      release();
    });
  } else {
    window.location.assign(to);
    clearTimeout(watchdog);
    release();
  }
}

/*
 * The page to return to after re-login. Captured from live location, but never
 * an auth route: if the user is already on `/login` (or the destination would
 * resolve to it) we omit it so the login screen doesn't redirect back to
 * itself in a loop.
 */
function safeDestination(): string | undefined {
  const path = window.location.pathname + window.location.search;
  if (
    isAuthRoute(window.location.pathname) ||
    window.location.pathname === '/login'
  ) {
    return undefined;
  }
  return path;
}

export function handleError(error: unknown): void {
  if (!(error instanceof ApiError)) return;

  // Expected auth-flow 4xx (e.g. a failed login) are handled locally.
  if (isAuthRoute(error.url)) return;

  telemetry.recordError(error, { code: error.code, status: error.status });

  if (error.status === 401) {
    // EXPIRY / no session established (finding 1.12). A 401 means the session
    // is not (or no longer) established — routine expiry, NOT a refusal of an
    // act. For a BROWSER the BFF takes an expired/no-session request to the
    // sign-in screen (302 → /sign-in); the machine 401 the FE sees here is the
    // same thing. So we do NOT announce an access refusal and do NOT fire
    // access_revoked telemetry — we simply take the user to sign-in.
    //
    // The backend emits one generic Problem body (type about:blank) for every
    // refusal cause by design and does not carry a machine-readable code to
    // distinguish them (BFF #8 / domain #14); so the FE branches on HTTP status
    // and the browser-redirect, never on a code string. See the 403 branch and
    // verification.md: finer FE cause-separation would be a backend change
    // (a distinct Problem type), not an FE guess.
    //
    // Capture the pre-expiry page BEFORE clearing anything — after the clear
    // the router may have already moved, and the destination must never be
    // /login itself.
    // Already on the sign-in screen: a 401 is the expected no-session state,
    // not a revocation. Clearing the cache here drops the /me query the login
    // page observes, which refetches on the still-mounted page and loops
    // (401 -> clear -> refetch -> 401). Leave the page to its own state.
    if (window.location.pathname === '/login') return;
    const destination = safeDestination();
    // Clear the derived session synchronously, then tear down the cache and
    // redirect. Cancellation is AWAITED before the cache clear so a query that
    // is already in flight for the expired session cannot resolve back into the
    // cache after we clear it (a shared-machine leak). setUser(null) drops RUM
    // attribution so activity after the expiry is not logged under the
    // signed-out user, matching the explicit-logout path.
    useAuthStore.getState().clear();
    telemetry.setUser(null);
    void (async () => {
      await queryClient.cancelQueries();
      queryClient.clear();
    })();
    guardedRedirect('/login', destination);
    return;
  }

  if (error.status === 403) {
    // PER-ACT DENIAL (finding 1.12). An authenticated-but-refused act: the one
    // generic refusal is announced to a screen reader where the operator is
    // working (R60 AC-8b85b662), and that is ALL. We deliberately do NOT purge
    // the access-scoped caches and do NOT redirect to /no-access — a single
    // denied act is not a loss of all access, and dumping the operator at
    // /no-access for one refused control was the bug.
    //
    // A 403 cannot be told apart from a true revocation from the response: the
    // backend returns ONE generic body (domain genericAccessRefusalBody, type
    // about:blank) for both by design, and distinguishing the cause is a
    // blocked requirement (AC-403f975c/AC-f5d40272). So TRUE REVOCATION is NOT
    // driven off a route 403; it is driven off the /me freshness bridge
    // (finding 1.6): when the session stops resolving the 401 path above takes
    // over, and when access narrows the bridge re-syncs the store so the guards
    // hide what is no longer permitted. If finer FE separation is ever wanted,
    // it is a backend change (a distinct Problem type), not an FE guess
    // (recorded in verification.md).
    announceRefusal();
    return;
  }
}

/*
 * NOTE on the access-scoped cache teardown (findings 1.5 + 1.12). An earlier
 * design purged an allowlist-inverted set of caches and routed to /no-access on
 * every 403. The corrected 1.12 model (confirmed against BFF #8 / domain #14:
 * one generic Problem body, no cause code) does NOT purge on a route 403 — a
 * per-act denial only announces, in place. TRUE revocation is driven off the
 * /me freshness bridge: when the session stops resolving, the 401 path above
 * runs `queryClient.clear()` (a FULL clear — strictly safer than any allowlist
 * and inherently new-namespace-safe, which was finding 1.5's concern), and when
 * access merely narrows the bridge re-syncs the store so the guards hide what
 * is no longer permitted. So there is no separate scoped-purge function here.
 */

/*
 * Retry policy (spec §13). TanStack passes a 1-based failureCount (1 after the
 * first failure), so `failureCount < N` allows up to N attempts beyond the
 * initial call.
 *
 *   listed 4xx [400,401,403,404,422]  never retry (permanent client errors)
 *   429 (rate limited)                retry, bounded — waits per retryDelay
 *   408 (request timeout)             retry exactly once
 *   501 (not implemented)             never retry
 *   other 5xx                         up to 3
 *   network error (no ApiError)       up to 3
 */
const MAX_RETRIES = 3;
const RETRY_DELAY_CAP_MS = 30_000;
/*
 * Minimum backoff floor for a 429 whose Retry-After resolves to 0 or a past
 * instant (routine clock skew between the BFF and browser). Without it, a
 * `Retry-After` HTTP-date that is already in the past would retry immediately,
 * producing a synchronized burst against an already-rate-limited backend.
 */
const RETRY_DELAY_FLOOR_MS = 1_000;

export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError) {
    if ([400, 401, 403, 404, 422].includes(error.status)) return false;
    if (error.status === 501) return false;
    if (error.status === 408) return failureCount < 1;
    // 429 and other 5xx fall through to the bounded default.
  }
  return failureCount < MAX_RETRIES;
}

/*
 * Backoff (spec §13). For a 429 carrying Retry-After, honor that delay
 * (seconds → ms) but keep it BOUNDED on both ends: floor a 0/past value to
 * RETRY_DELAY_FLOOR_MS so clock skew can't trigger an instant retry storm, and
 * cap a huge/hostile value at RETRY_DELAY_CAP_MS so it can't freeze the query
 * for hours. Otherwise bounded exponential backoff: min(1000·2^n, cap), where
 * n is the 0-based attempt (failureCount − 1).
 */
export function retryDelay(failureCount: number, error: unknown): number {
  if (error instanceof ApiError && error.retryAfter !== undefined) {
    const requestedMs = error.retryAfter * 1000;
    const floored = Math.max(RETRY_DELAY_FLOOR_MS, requestedMs);
    return Math.min(floored, RETRY_DELAY_CAP_MS);
  }
  const attempt = Math.max(0, failureCount - 1);
  return Math.min(1000 * 2 ** attempt, RETRY_DELAY_CAP_MS);
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleError }),
  mutationCache: new MutationCache({ onError: handleError }),
  defaultOptions: {
    queries: {
      retry: shouldRetry,
      retryDelay,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});

/*
 * Keep the derived auth store in step with the live /me query.
 *
 * The store is the imperative projection route guards read via getState()
 * outside React (admin gate, the entity edit gates). It is seeded on cold
 * arrival (_auth beforeLoad) and at login, but /me has a short stale time and
 * refetches on access — after a mid-session role change, the guards would keep
 * reading a role the server no longer grants until a hard reload. This bridges
 * the reactive query into the imperative store.
 *
 * A QueryCache subscription is used rather than a component effect precisely
 * BECAUSE it runs outside React: there is no render to trigger, so no
 * render-loop risk. It fires only for the /me query and only when its data
 * actually changed (role or capabilities), so a routine refetch of an
 * unchanged identity writes nothing and no store subscriber churns.
 */
const meQueryHash = hashKey(keys.session.me());

/*
 * Compare the WHOLE security-relevant projection the guards and nav read, not
 * just role + capabilities (finding 6). This PR bases the admin-area gates and
 * nav on `isAdministrator` and makes `organizationName` required; a
 * privilege-only change or a same-role profile switch (changed `id` /
 * `organizationName`) must re-sync the store too, or the imperative projection
 * the guards read via getState() goes stale until a hard reload. Everything a
 * gate or the nav reads off the session is compared here.
 */
function isSameSession(a: CurrentUser, b: CurrentUser): boolean {
  return (
    a.id === b.id &&
    a.role === b.role &&
    a.isAdministrator === b.isAdministrator &&
    a.organizationName === b.organizationName &&
    a.capabilities.length === b.capabilities.length &&
    a.capabilities.every((cap, i) => cap === b.capabilities[i])
  );
}

queryClient.getQueryCache().subscribe((event) => {
  if (event.type !== 'updated' || event.query.queryHash !== meQueryHash) return;
  const next = event.query.state.data as CurrentUser | undefined;
  if (next === undefined) return;
  const current = useAuthStore.getState().session;
  if (current && isSameSession(current, next)) return;
  useAuthStore.getState().setSession(next);
});
